// src/pages/ResendLink.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  Container,
} from "@mui/material";
import { Email, CheckCircle, ArrowBack } from "@mui/icons-material";
import { useMutation } from "react-query";
import candidateService from "../../api/services/candidate";

function ResendLink() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const resendMutation = useMutation(
    (email) => candidateService.resendSetupLink(email),
    {
      onSuccess: (data) => {
        if (!data.success) {
          setError(data.error);
        }
      },
      onError: (error) => {
        setError(error.message || 'Failed to resend link');
      }
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setError('');
    resendMutation.mutate(email);
  };

  if (resendMutation.isSuccess) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#F1F5F9",
          py: 4,
          px: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 480,
            width: "100%",
            borderRadius: 4,
            p: 4,
            boxShadow: "0 20px 60px rgba(0,0,0,0.06)",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                bgcolor: "#DCFCE7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <CheckCircle sx={{ color: "#16A34A", fontSize: 36 }} />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#0F172A",
                mb: 1,
              }}
            >
              Link Sent!
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#64748B",
                mb: 3,
              }}
            >
              A new password setup link has been sent to your email.
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate('/login')}
              sx={{
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "#2563EB",
                "&:hover": {
                  bgcolor: "#1D4ED8",
                },
              }}
            >
              Go to Login
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#F1F5F9",
        py: 4,
        px: 2,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -200,
          right: -200,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(37, 99, 235, 0.06) 0%, transparent 70%)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -200,
          left: -200,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(37, 99, 235, 0.04) 0%, transparent 70%)",
        }}
      />

      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            p: { xs: 3, sm: 5 },
            boxShadow: "0 20px 60px rgba(0,0,0,0.06)",
            position: "relative",
          }}
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/login')}
            sx={{
              mb: 3,
              color: "#64748B",
              textTransform: "none",
              fontWeight: 500,
              "&:hover": {
                color: "#0F172A",
                bgcolor: "transparent",
              },
            }}
          >
            Back to Login
          </Button>

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#0F172A",
                mb: 0.5,
                letterSpacing: "-0.02em",
              }}
            >
              Resend Setup Link
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#64748B", fontWeight: 400 }}
            >
              Enter your email to receive a new password setup link
            </Typography>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email Address"
              variant="outlined"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              size="medium"
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#F8FAFC",
                  "&:hover": {
                    bgcolor: "#F1F5F9",
                  },
                  "&.Mui-focused": {
                    bgcolor: "#FFFFFF",
                    boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.06)",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#64748B",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: "#94A3B8", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={resendMutation.isLoading}
              sx={{
                py: 1.75,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                bgcolor: "#2563EB",
                "&:hover": {
                  bgcolor: "#1D4ED8",
                  boxShadow: "0 4px 16px rgba(37, 99, 235, 0.3)",
                },
                "&:disabled": {
                  bgcolor: "#93C5FD",
                },
              }}
            >
              {resendMutation.isLoading ? <CircularProgress size={24} sx={{ color: "#FFFFFF" }} /> : "Send Link"}
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}

export default ResendLink;